import React, { useCallback, useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { FormProvider, useForm, Controller } from 'react-hook-form'
import { navigate, useParams } from '@reach/router'
import HeaderTitle from '_components/header-title'
import { Grid, InputLabel, TextField, FormHelperText, Link, Typography } from '@material-ui/core'
import { Button as ReferaButton, Loader } from '@refera/ui-web'
import Button from '_components/button'
import Svg from '_components/svg'
import EditIcon from '_assets/icons/ic-edit.svg'
import { BasicInput, Select } from '_/components/inputs'
import classNames from 'classnames'
import { useDialog } from '_/hooks/use-dialog'
import { useToast } from '_/hooks/use-toast'
import { getAgencyWebhookById, patchWebhook } from '_/modules/agency/actions'
import useRolePermission from '_/hooks/use-role-permission'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'
import { HeaderTable } from './HeaderTable'

import useStyles from './styles'

const JSON_TO_SEND_OPTIONS = [
  { label: 'Enviar o padrão', value: 'send_default' },
  { label: 'Enviar o personalizado', value: 'send_specific' },
]

const HTTP_METHOD_OPTIONS = [
  { label: 'GET', value: 'GET' },
  { label: 'POST', value: 'POST' },
]

const AgencyWebhook = ({ isEditing = true, setIsEditing }) => {
  const styles = useStyles()
  const dispatch = useDispatch()
  const { agencyId, id } = useParams()
  const { showToast } = useToast()
  const { showDialog, closeDialog } = useDialog()
  const { checkIfUserDoesNotPermission, isAdmin, isAdminSAAS } = useRolePermission()

  const [isLoading, setIsLoading] = useState(false)
  const [webhookData, setWebhookData] = useState({})

  const methods = useForm({ mode: 'all' })
  const {
    watch,
    control,
    reset,
    setValue,
    getValues,
    handleSubmit,
    formState: { dirtyFields, errors, isValid },
  } = methods

  const showJsonDefinition = watch('jsonToSend') === 'send_specific'
  const showDefaultJsonSelection = watch('jsonToSend') === 'send_default'
  const selectedDefaultJson = watch('selectedDefaultJson')

  // Criar opções do webhook_json se disponível
  const webhookJsonOptions = webhookData?.webhookJson
    ? Object.keys(webhookData.webhookJson).map(key => ({
        value: key,
        label: webhookData.webhookJson[key]?.desc || key,
      }))
    : []

  const handleBackNavigation = useCallback(() => {
    navigate(-1)
  }, [])

  const handleCancel = () => {
    if (Object.keys(dirtyFields).length > 0) {
      showDialog({
        type: 'warning',
        subject: 'Você tem certeza que deseja cancelar estas alterações?',
        labelApprove: 'Sim',
        labelCancel: 'Não',
        onApprove: () => {
          closeDialog()
          handleBackNavigation()
        },
        onCancel: closeDialog,
      })
    } else {
      handleBackNavigation()
    }
  }

  useEffect(() => {
    if (id) {
      dispatch(getAgencyWebhookById(id, { agency: agencyId }))
        .then(data => {
          if (data?.agency?.toString() !== agencyId?.toString()) {
            handleBackNavigation()
            return
          }
          setWebhookData(data)
        })
        .catch(() => {
          handleBackNavigation()
        })
    }
  }, [id, agencyId, handleBackNavigation])

  const getJsonToSendValue = (jsonToSend, jsonDefinition) => {
    const hasJsonDefinition =
      jsonDefinition &&
      ((typeof jsonDefinition === 'object' && Object.keys(jsonDefinition).length > 0) ||
        (typeof jsonDefinition === 'string' && jsonDefinition.trim() !== ''))

    if (hasJsonDefinition) {
      return 'send_specific'
    }

    if (typeof jsonToSend === 'object' && jsonToSend?.id) {
      return 'send_default'
    }
    if (typeof jsonToSend === 'string') {
      return jsonToSend
    }
    return 'send_default'
  }

  const getSelectedDefaultJson = (jsonToSend, webhookJson) => {
    if (typeof jsonToSend === 'object' && jsonToSend?.id) {
      return (
        Object.keys(webhookJson || {}).find(key => webhookJson[key]?.id === jsonToSend.id) || ''
      )
    }
    return ''
  }

  function headersObjectToArray(headers) {
    const parsed = typeof headers === 'string' ? JSON.parse(headers || '{}') : headers || {}

    const entries = Object.entries(parsed).map(([key, value]) => ({
      key: { label: key, value: key },
      value,
    }))

    return entries.length ? entries : [{ key: null, value: '' }]
  }

  useEffect(() => {
    if (webhookData) {
      reset({
        information: webhookData?.trigger?.whInformation || '',
        trigger: webhookData?.trigger?.whTrigger || '',
        apiCall: webhookData?.apiCall || '',
        httpMethod: webhookData?.httpMethod || 'POST',
        returnSuccess: webhookData?.returnSuccess || '',
        returnError: webhookData?.returnError || '',
        jsonToSend: getJsonToSendValue(webhookData?.jsonToSend, webhookData?.jsonDefinition),
        jsonDefinition: webhookData?.jsonDefinition || '',
        selectedDefaultJson: getSelectedDefaultJson(
          webhookData?.jsonToSend,
          webhookData?.webhookJson
        ),
        headers: headersObjectToArray(webhookData?.headers),
      })
    }
  }, [webhookData, reset])

  const handleJsonFieldChange = onChange => e => {
    const inputValue = e.target.value

    try {
      const parsed = JSON.parse(inputValue)
      onChange(parsed)
    } catch {
      onChange(inputValue)
    }
  }

  useEffect(() => {
    if (showJsonDefinition) {
      const currentJsonDefinition = getValues('jsonDefinition')
      if (currentJsonDefinition && currentJsonDefinition.toString().trim() !== '') {
        return
      }

      if (webhookData?.jsonDefinition) {
        const jsonString =
          typeof webhookData.jsonDefinition === 'object'
            ? JSON.stringify(webhookData.jsonDefinition, null, 2)
            : webhookData.jsonDefinition

        setValue('jsonDefinition', jsonString, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: true,
        })
        return
      }

      if (webhookData?.webhookJson) {
        const jsonItems = Array.isArray(webhookData.webhookJson)
          ? webhookData.webhookJson
          : Object.values(webhookData.webhookJson)

        let targetItem = jsonItems.find(item => item?.desc?.toLowerCase().includes('v2'))
        if (!targetItem) {
          targetItem = jsonItems.find(item => item?.desc?.toLowerCase().includes('v1'))
        }
        if (!targetItem && jsonItems.length > 0) {
          ;[targetItem] = jsonItems
        }

        if (targetItem && targetItem.json) {
          const jsonString = JSON.stringify(targetItem.json, null, 2)

          setValue('jsonDefinition', jsonString, {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: true,
          })
        } else {
          showToast({
            type: 'warning',
            message: 'Nenhum JSON padrão disponível para preencher o campo.',
          })
        }
      }
    }
  }, [showJsonDefinition, webhookData, getValues, setValue])

  function handleValidationError() {
    showToast({
      type: 'error',
      message: 'Um ou mais dados do formulário possuem problemas.',
    })
  }

  const onSubmit = useCallback(
    data => {
      // Verificar se o usuário tem permissão para salvar webhooks
      // Permitir se: é admin OU tem permissão intermediary_configs OU intermediary OU intermediary_registry
      if (isAdmin || isAdminSAAS) {
        // Admin pode salvar diretamente
        executeSubmit(data)
      } else {
        // Verificar permissões específicas para não-admins
        const hasIntermediaryConfigs = checkIfUserDoesNotPermission({
          permission: PERMISSIONS.INTERMEDIARY_CONFIGS,
          permissionAction: [PERMISSIONS_ACTIONS.ADD, PERMISSIONS_ACTIONS.EDIT],
        })
        const hasIntermediary = checkIfUserDoesNotPermission({
          permission: PERMISSIONS.INTERMEDIARY,
          permissionAction: [PERMISSIONS_ACTIONS.ADD, PERMISSIONS_ACTIONS.EDIT],
        })
        const hasIntermediaryRegistry = checkIfUserDoesNotPermission({
          permission: PERMISSIONS.INTERMEDIARY_REGISTRY,
          permissionAction: [PERMISSIONS_ACTIONS.ADD, PERMISSIONS_ACTIONS.EDIT],
        })

        if (!hasIntermediaryConfigs || !hasIntermediary || !hasIntermediaryRegistry) {
          executeSubmit(data)
        } else {
          showToast({
            type: 'error',
            message: 'Você não tem permissão para realizar esta ação.',
          })
        }
      }

      function headersArrayToObject(headers = []) {
        return headers.reduce((acc, { key, value }) => {
          const headerKey = key?.label?.trim()
          if (!headerKey) return acc
          acc[headerKey] = value
          return acc
        }, {})
      }

      function executeSubmit(formData) {
        setIsLoading(true)

        const processedData = { ...formData }

        const hasValidHeaders =
          Array.isArray(formData?.headers) && formData.headers.some(h => h?.key?.label?.trim())

        processedData.headers = hasValidHeaders
          ? JSON.stringify(headersArrayToObject(formData.headers))
          : {}

        if (formData.jsonToSend === 'send_default' && formData.selectedDefaultJson) {
          const selectedJson = webhookData?.webhookJson?.[formData.selectedDefaultJson]
          if (selectedJson?.id) {
            processedData.jsonToSend = selectedJson.id
          }
          // Limpar jsonDefinition quando usar padrão
          processedData.jsonDefinition = null
        } else if (formData.jsonToSend === 'send_specific') {
          if (formData.jsonDefinition) {
            // A validação de sintaxe já foi feita pelo React Hook Form
            // Apenas converte para objeto se for STRING
            processedData.jsonDefinition =
              typeof formData.jsonDefinition === 'string'
                ? JSON.parse(formData.jsonDefinition)
                : formData.jsonDefinition
          }
        }

        delete processedData.selectedDefaultJson

        dispatch(patchWebhook(id, processedData))
          .then(() => {
            showToast({ type: 'success' })
            setIsLoading(false)
          })
          .catch(() => {
            showToast({ type: 'error' })
            setIsLoading(false)
          })
      }
    },
    [
      isValid,
      webhookData,
      checkIfUserDoesNotPermission,
      isAdmin,
      isAdminSAAS,
      id,
      dispatch,
      showToast,
    ]
  )

  return (
    <>
      <Loader open={isLoading} hasBackdrop />
      <FormProvider {...methods}>
        <form id="webhookForm" onSubmit={handleSubmit(onSubmit, handleValidationError)}>
          <HeaderTitle title="Webhook" backButtonAction={handleBackNavigation}>
            <Grid className={styles.headerButtons}>
              {isEditing && (
                <>
                  <ReferaButton color="red" variant="secondary" onClick={handleCancel}>
                    Cancelar
                  </ReferaButton>
                  <Button
                    className={styles.button}
                    type="submit"
                    form="webhookForm"
                    color="primary"
                    variant="contained"
                    disabled={isLoading || !isValid}
                  >
                    Salvar
                  </Button>
                </>
              )}
              {!isEditing && (
                <Button
                  className={styles.button}
                  onClick={() => {
                    // Verificar se o usuário tem permissão para editar webhooks
                    // Permitir se: é admin OU tem permissão intermediary_configs OU intermediary OU intermediary_registry
                    if (isAdmin || isAdminSAAS) {
                      setIsEditing(true)
                    } else {
                      // Verificar permissões específicas para não-admins
                      const hasIntermediaryConfigs = checkIfUserDoesNotPermission({
                        permission: PERMISSIONS.INTERMEDIARY_CONFIGS,
                        permissionAction: [PERMISSIONS_ACTIONS.ADD, PERMISSIONS_ACTIONS.EDIT],
                      })
                      const hasIntermediary = checkIfUserDoesNotPermission({
                        permission: PERMISSIONS.INTERMEDIARY,
                        permissionAction: [PERMISSIONS_ACTIONS.ADD, PERMISSIONS_ACTIONS.EDIT],
                      })
                      const hasIntermediaryRegistry = checkIfUserDoesNotPermission({
                        permission: PERMISSIONS.INTERMEDIARY_REGISTRY,
                        permissionAction: [PERMISSIONS_ACTIONS.ADD, PERMISSIONS_ACTIONS.EDIT],
                      })

                      if (!hasIntermediaryConfigs || !hasIntermediary || !hasIntermediaryRegistry) {
                        setIsEditing(true)
                      } else {
                        showToast({
                          type: 'error',
                          message: 'Você não tem permissão para realizar esta ação.',
                        })
                      }
                    }
                  }}
                  color="primary"
                  variant="outlined"
                  endIcon={<Svg icon={EditIcon} className={styles.editIcon} />}
                >
                  Editar
                </Button>
              )}
            </Grid>
          </HeaderTitle>
          <Grid className={styles.container}>
            <Grid className={`${styles.subcontainer} ${styles.overrideAlign}`}>
              <Grid className={styles.headerContainer}>
                <HeaderTable />
              </Grid>
            </Grid>
            <Grid className={styles.subcontainer}>
              <Grid className={styles.wrapper}>
                <Grid className={styles.input}>
                  <BasicInput
                    label=" Informação"
                    name="information"
                    labelClasses={styles.label}
                    disabled
                    required
                    rules={{
                      maxLength: {
                        value: 50,
                        message: `Esse campo aceita no máximo 50 caracteres`,
                      },
                    }}
                  />
                </Grid>
                <Grid className={styles.input}>
                  <BasicInput
                    required
                    label=" Gatilho"
                    name="trigger"
                    disabled
                    labelClasses={styles.label}
                    rules={{
                      maxLength: {
                        value: 100,
                        message: `Esse campo aceita no máximo 100 caracteres`,
                      },
                    }}
                  />
                </Grid>
                <Grid className={styles.input}>
                  <Typography style={{ marginTop: 18 }}>
                    <Link
                      href="https://docs.google.com/document/d/1EwTuuww_XyXkxidCeqHIzPppcq5ncF4EmCq_VzGo7SE/edit?tab=t.0#heading=h.brxeowtz4zau"
                      target="_blank"
                    >
                      Documentação técnica de mapeamento
                    </Link>
                  </Typography>
                </Grid>
              </Grid>

              <Grid className={styles.wrapper}>
                <Grid className={styles.input}>
                  <Select
                    label="Método HTTP"
                    name="httpMethod"
                    defaultValue={webhookData?.httpMethod || 'POST'}
                    labelClasses={classNames(styles.inputLabel, styles.selectLabel)}
                    options={HTTP_METHOD_OPTIONS}
                    getKey={item => item.value}
                    getValue={item => item.value}
                    getLabel={item => item.label}
                    required
                  />
                </Grid>
                <Grid className={styles.input}>
                  <InputLabel variant="body2" id="apiCall-label" className={styles.label}>
                    API para chamar
                    <span className={styles.required}>*</span>
                  </InputLabel>
                  <Controller
                    name="apiCall"
                    control={control}
                    rules={{
                      required: 'Esse campo é obrigatório',
                    }}
                    render={({ value, name, onBlur, onChange }) => {
                      return (
                        <TextField
                          name={name}
                          value={value}
                          onChange={onChange}
                          fullWidth
                          multiline
                          variant="outlined"
                          maxRows={10}
                          minRows={3}
                          className={styles.input}
                          onBlur={onBlur}
                          error={!!errors?.apiCall}
                          required
                        />
                      )
                    }}
                  />
                  <FormHelperText className={styles.errorMessage} error={!!errors?.apiCall}>
                    {errors?.apiCall?.message}
                  </FormHelperText>
                </Grid>
                <Grid className={styles.input}>
                  <InputLabel variant="body2" id="returnSuccess-label" className={styles.label}>
                    Retornos de sucesso
                  </InputLabel>
                  <Controller
                    name="returnSuccess"
                    control={control}
                    render={({ value, name, onChange }) => {
                      return (
                        <TextField
                          name={name}
                          value={value}
                          onChange={onChange}
                          fullWidth
                          multiline
                          variant="outlined"
                          maxRows={10}
                          minRows={3}
                          className={styles.input}
                          inputProps={{ maxLength: 100 }}
                        />
                      )
                    }}
                  />
                </Grid>
                <Grid className={styles.input}>
                  <InputLabel variant="body2" id="returnError-label" className={styles.label}>
                    Retornos de erro
                  </InputLabel>
                  <Controller
                    name="returnError"
                    control={control}
                    render={({ value, name, onChange }) => {
                      return (
                        <TextField
                          name={name}
                          value={value}
                          onChange={onChange}
                          fullWidth
                          multiline
                          variant="outlined"
                          maxRows={10}
                          minRows={3}
                          className={styles.input}
                          inputProps={{ maxLength: 100 }}
                        />
                      )
                    }}
                  />
                </Grid>
                <Grid className={classNames(styles.input, styles.alignTop)}>
                  <Controller
                    name="jsonToSend"
                    control={control}
                    defaultValue="send_default"
                    rules={{ required: 'Este campo é obrigatório' }}
                    render={({ value, onChange }) => (
                      <Select
                        label="Json para enviar "
                        name="jsonToSend"
                        value={value || 'send_default'}
                        onChange={onChange}
                        labelClasses={classNames(styles.inputLabel, styles.selectLabel)}
                        options={JSON_TO_SEND_OPTIONS}
                        getKey={item => item.value}
                        getValue={item => item.value}
                        getLabel={item => item.label}
                        required
                      />
                    )}
                  />
                </Grid>
                {showDefaultJsonSelection && (
                  <Grid className={styles.input}>
                    <Controller
                      name="selectedDefaultJson"
                      control={control}
                      rules={{
                        required: {
                          value: showDefaultJsonSelection,
                          message:
                            'Este campo é obrigatório quando "Enviar o padrão" está selecionado',
                        },
                      }}
                      render={({ value, onChange }) => (
                        <Select
                          label="Selecionar JSON padrão"
                          name="selectedDefaultJson"
                          labelClasses={classNames(styles.inputLabel, styles.selectLabel)}
                          options={webhookJsonOptions}
                          getKey={item => item.value}
                          getValue={item => item.value}
                          getLabel={item => item.label}
                          required={showDefaultJsonSelection}
                          value={value || ''}
                          onChange={onChange}
                        />
                      )}
                    />
                    {webhookJsonOptions.length === 0 && (
                      <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                        Nenhuma opção de JSON disponível
                      </div>
                    )}
                  </Grid>
                )}
                {showDefaultJsonSelection &&
                  selectedDefaultJson &&
                  webhookData?.webhookJson?.[selectedDefaultJson] && (
                    <Grid className={styles.jsonDefinition}>
                      <InputLabel
                        variant="body2"
                        id="defaultJsonPreview-label"
                        className={styles.label}
                      >
                        Visualização do JSON padrão
                      </InputLabel>
                      <TextField
                        value={JSON.stringify(
                          webhookData.webhookJson[selectedDefaultJson],
                          null,
                          2
                        )}
                        fullWidth
                        multiline
                        variant="outlined"
                        minRows={10}
                        maxRows={20}
                        InputProps={{
                          readOnly: true,
                        }}
                        className={styles.readOnlyField}
                      />
                    </Grid>
                  )}
                {showJsonDefinition && (
                  <Grid className={styles.jsonDefinition}>
                    <InputLabel variant="body2" id="jsonDefinition-label" className={styles.label}>
                      Json específico
                    </InputLabel>
                    <Controller
                      name="jsonDefinition"
                      control={control}
                      rules={{
                        required: {
                          value: showJsonDefinition,
                          message:
                            'O JSON personalizado é obrigatório quando esta opção está selecionada',
                        },
                        validate: value => {
                          if (!showJsonDefinition) return true
                          try {
                            JSON.parse(typeof value === 'string' ? value : JSON.stringify(value))
                            return true
                          } catch (e) {
                            return 'JSON inválido. Por favor, corrija a sintaxe.'
                          }
                        },
                      }}
                      render={({ value, name, onChange }) => {
                        const formattedValue =
                          typeof value === 'string' ? value : JSON.stringify(value, null, 2)
                        return (
                          <TextField
                            name={name}
                            value={formattedValue}
                            onChange={handleJsonFieldChange(onChange)}
                            fullWidth
                            multiline
                            variant="outlined"
                            minRows={20}
                            maxRows={30}
                            error={!!errors?.jsonDefinition}
                            helperText={errors?.jsonDefinition?.message}
                          />
                        )
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </>
  )
}

export default AgencyWebhook
